import {ComponentType, FocusEvent, RefObject, useCallback} from 'react';
import {TextFieldProps} from '../TextField';
import type {BaseDatePickerProps} from './BaseDatePicker';
import Flatpickr from 'react-flatpickr';
import {useId} from '../hooks';
import {useFlatpickr} from './use-flatpickr';
import {useDateInput} from './use-date-input';

export type DatePickerInputProps<T = TextFieldProps> = {
    renderRef: (node: HTMLInputElement | null) => void;
    InputComponent: ComponentType<T>;
    InputProps?: T;
    id?: string;
    size: BaseDatePickerProps['size'];
    value?: string | number | Date;
    placeholder?: string;
    fpRef: RefObject<Flatpickr['flatpickr']>;
    dateFormat: string;
};

export function DatePickerInput(props: DatePickerInputProps): React.ReactElement {
    const {
        InputComponent,
        InputProps,
        id,
        size,
        renderRef,
        placeholder,
        value,
        dateFormat,
        fpRef
    } = props;
    const generatedId = useId(id, 'datePicker');
    const {
        parseDate,
        setDate,
        formatDate,
        clearDate,
        isCalendarElement
    } = useFlatpickr({
        dateFormat,
        fpRef
    });
    const {
        inputValue,
        handleBlur,
        handleChange
    } = useDateInput({
        formatDate,
        parseDate,
        value
    });

    const setDateIfValid = useCallback((e: FocusEvent<HTMLInputElement>): void => {
        if (!isCalendarElement(e.relatedTarget)) {
            if (parseDate(e.target.value)) {
                setDate(inputValue, true);
            } else {
                clearDate(true);
            }
        }
    }, [setDate, inputValue, isCalendarElement, parseDate, clearDate]);

    return (
        <div
            data-testid='input'
            ref={renderRef}
        >
            <InputComponent
                size={size}
                {...InputProps}
                autoComplete='off'
                id={generatedId}
                onBlur={(e: FocusEvent<HTMLInputElement>): void => {
                    handleBlur(e);
                    setDateIfValid(e);
                }}
                onChange={handleChange}
                placeholder={placeholder}
                value={inputValue}
            />
        </div>
    );
}
